import type { TelemetrySink } from "@onetasklist/shared";

/** The slice of the Application Insights SDK the sink uses. */
export interface ApplicationInsightsClient {
  trackEvent(event: { name: string }, properties?: Record<string, string>): void;
  trackException(
    exception: { exception: Error },
    properties?: Record<string, string>,
  ): void;
  setAuthenticatedUserContext(
    authenticatedUserId: string,
    accountId?: string,
    storeInCookie?: boolean,
  ): void;
  clearAuthenticatedUserContext(): void;
  /** Records the current screen. Route tracking only covers later navigations, so the first one is sent by hand. */
  trackPageView(): void;
  flush(): void;
}

/** The parts of a telemetry envelope the filters read and rewrite. */
export interface TelemetryEnvelope {
  baseType?: string;
  baseData?: {
    exceptions?: { message?: string }[];
    message?: string;
    name?: string;
    uri?: string;
    refUri?: string;
    target?: string;
    data?: string;
    properties?: Record<string, unknown>;
  };
  ext?: { trace?: { name?: string } };
}

const GUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * A URL or path with its query string and fragment cut off and every GUID replaced by `:id`. Query
 * strings carry search text and the sign-in callback's authorisation code; ids say whose board it is.
 */
export const scrubUrl = (value: string): string =>
  value.split(/[?#]/, 1)[0].replace(GUID, ":id");

/** A screen's name from its URL: the scrubbed path, e.g. `/:id/tasks`. Never the page title. */
export const routeName = (uri: string | undefined): string => {
  if (!uri) return "unknown";
  try {
    return scrubUrl(new URL(uri, window.location.origin).pathname);
  } catch {
    return "unknown";
  }
};

const PAGE_TYPES = new Set(["PageviewData", "PageviewPerformanceData"]);
const URL_FIELDS = ["uri", "refUri", "target", "data"] as const;
const URL_PROPERTIES = ["url", "uri", "refUri", "errorSrc"];

/**
 * Rewrites telemetry in place so it carries no user content: page views are named by route instead
 * of by title (titles hold board names), and every URL loses its query, fragment and ids.
 */
export const scrubEnvelope = (envelope: TelemetryEnvelope): void => {
  const data = envelope.baseData;
  if (data) {
    if (envelope.baseType && PAGE_TYPES.has(envelope.baseType)) {
      data.name = routeName(data.uri);
    } else if (typeof data.name === "string") {
      data.name = scrubUrl(data.name);
    }

    for (const field of URL_FIELDS) {
      const value = data[field];
      if (typeof value === "string") data[field] = scrubUrl(value);
    }

    const properties = data.properties;
    if (properties) {
      for (const key of URL_PROPERTIES) {
        const value = properties[key];
        if (typeof value === "string") properties[key] = scrubUrl(value);
      }
    }
  }

  const trace = envelope.ext?.trace;
  if (trace && typeof trace.name === "string") trace.name = scrubUrl(trace.name);
};

// Browsers raise this when a ResizeObserver callback causes another resize within a frame. It is
// harmless, reported through window.onerror, and constant on a board full of resizing cards.
const BENIGN_ERRORS = [/ResizeObserver loop/i];

/** False for telemetry to drop before it is sent: known-harmless browser errors. */
export const isWorthSending = (envelope: TelemetryEnvelope): boolean => {
  if (envelope.baseType !== "ExceptionData") return true;
  const messages = [
    envelope.baseData?.message,
    ...(envelope.baseData?.exceptions ?? []).map((e) => e.message),
  ].filter((m): m is string => typeof m === "string");
  return !messages.some((m) => BENIGN_ERRORS.some((pattern) => pattern.test(m)));
};

/** Adapts the SDK to the platform-agnostic sink shared code reports through. */
export const createApplicationInsightsSink = (
  client: ApplicationInsightsClient,
): TelemetrySink => ({
  trackEvent: (name, properties) => client.trackEvent({ name }, properties),
  trackException: (error, properties) =>
    client.trackException({ exception: error }, properties),
});

/**
 * Starts the Application Insights browser SDK. Loaded on demand so a region without a connection
 * string never downloads it.
 *
 * Cookies and browser storage are off: no consent banner exists, and the authenticated user id
 * is what joins a user's sessions together instead. The privacy policy says we store only what
 * OneTaskList needs to work, so the SDK must not buffer unsent telemetry in session storage
 * either; telemetry still unsent when a tab closes is lost.
 */
export const startApplicationInsights = async (
  connectionString: string,
): Promise<ApplicationInsightsClient> => {
  const { ApplicationInsights } = await import(
    "@microsoft/applicationinsights-web"
  );

  const appInsights = new ApplicationInsights({
    config: {
      connectionString,
      disableCookiesUsage: true,
      isStorageUseDisabled: true,
      enableSessionStorageBuffer: false,
      enableAutoRouteTracking: true,
    },
  });
  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer((envelope) => {
    if (!isWorthSending(envelope)) return false;
    scrubEnvelope(envelope);
    return true;
  });

  return appInsights;
};
